import React, { useEffect } from "react";

import { INetskraflProps } from "./Netskrafl.types";
import { GlobalState } from "@/mithril/globalstate";
import { makeGlobalState } from "@/mithril/authSettings";
import { main } from "@/mithril/page";

export { type INetskraflProps };

const mountForUser = async (state: GlobalState): Promise<HTMLDivElement> => {
  // Return a DOM tree containing a mounted Netskrafl UI
  // for the user specified in the state object
  const { userEmail } = state;
  if (!userEmail) {
    // console.error("No user specified for Netskrafl UI");
    throw new Error("No user specified for Netskrafl UI");
  }
  // Check whether we already have a mounted UI for this user
  const elemId = `netskrafl-user-${userEmail}`;
  const existing = document.getElementById(elemId);
  if (existing) {
    // console.log("Netskrafl UI already mounted for user", userEmail);
    return existing as HTMLDivElement;
  }
  // Create a new div element to hold the UI
  const root = document.createElement('div');
  root.id = elemId;
  root.className = "netskrafl-loading";
  // Attach the partially-mounted div to the document body
  // as a placeholder while the UI is being mounted
  document.body.appendChild(root);
  const loginResult = await main(state, root);
  if (loginResult === "success") {
    // The UI was successfully mounted
    root.className = "netskrafl-user";
    return root;
  }
  // console.error("Failed to mount Netskrafl UI for user", userEmail);
  throw new Error("Failed to mount Netskrafl UI");
}

const NetskraflImpl = ({ state, tokenExpired }: INetskraflProps) => {
  const ref = React.createRef<HTMLDivElement>();
  const completeState = makeGlobalState({ ...state, tokenExpired });
  const { userEmail } = completeState;

  /*
  useEffect(() => {
    // Check whether the stylesheet is already present
    if (document.getElementById(CSS_LINK_ID)) return;
    // Load and link the stylesheet into the document head
    const link = document.createElement("link");
    const styleUrl = `${window.location.origin}/static/css/netskrafl.css`;
    link.id = CSS_LINK_ID;
    link.rel = "stylesheet";
    link.type = "text/css";
    link.href = styleUrl;
    document.head.appendChild(link);
    // We don't bother to remove the stylesheet when the component is unmounted
  }, []);
  */

  useEffect(() => {
    // Load the Netskrafl (Mithril) UI for a new user
    if (!userEmail) return;
    const container = ref.current;
    if (!container) {
      console.error("No container for Netskrafl UI");
      return;
    }
    const elemId = `netskrafl-user-${userEmail}`;
    if (container.firstElementChild?.id === elemId) {
      // The Netskrafl UI is already correctly mounted
      return;
    }
    try {
      mountForUser(completeState).then((div) => {
        // Attach the div as a child of the container
        // instead of any previous children
        const container = ref.current;
        if (container) {
          container.innerHTML = "";
          container.appendChild(div);
        }
      });
    } catch (err) {
      console.error("Failed to mount Netskrafl UI for user", userEmail);
      const container = document.getElementById("netskrafl-container");
      if (container)
        container.innerHTML = "";
    }
    return () => {
      // Move the Netskrafl UI to a hidden div under the body element
      // when the component is unmounted
      // console.log("Dismounting Netskrafl UI for user", userEmail);
      const container = document.getElementById("netskrafl-container");
      const div = container?.firstElementChild;
      if (div?.id === elemId) {
        document.body.appendChild(div);
      }
    };
  }, [userEmail]);

  return (
    <div className="netskrafl">
      <div className="netskrafl-container" id="netskrafl-container" ref={ref}>
        { /* The Netskrafl single-page UI will be rendered here */ }
      </div>
    </div>
  );
};

export const Netskrafl = React.memo(NetskraflImpl);
